import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Code2, Layers, Rocket, Settings2 } from "lucide-react";
import SEO from "../components/SEO";

const ADV_ICONS = [Layers, Rocket, Settings2, Code2];
const MODE_ICONS = [Code2, Settings2];

const IntegrationPage = () => {
  const { t } = useTranslation();
  const advItems = t("integration.advantage.items", { returnObjects: true });
  const timelineItems = t("integration.timeline.items", { returnObjects: true });
  const modes = t("integration.modes.items", { returnObjects: true });

  return (
    <div data-testid="integration-page">
      <SEO
        path="/integracion"
        title={t("seo.integration.title")}
        description={t("seo.integration.description")}
        keywords={t("seo.integration.keywords")}
      />
      {/* Hero */}
      <section className="hero" style={styles.hero}>
        <div className="container">
          <div className="heroInner" style={styles.heroInner}>
            <div className="eyebrow" style={styles.eyebrow}>{t("integration.hero.eyebrow")}</div>
            <h1 className="heroTitle" style={styles.heroTitle}>
              {t("integration.hero.titleLine1")}<br />{t("integration.hero.titleLine2")}
            </h1>
            <p className="heroSubtitle" style={styles.heroSubtitle}>{t("integration.hero.subtitle")}</p>
            <div className="ctaRow" style={styles.ctaRow}>
              <Link to="/contacto">
                <button className="btn-primary" data-testid="integration-hero-cta">
                  {t("common.talkToSales")}
                  <ArrowRight size={18} style={{ marginLeft: 8 }} />
                </button>
              </Link>
              <Link to="/producto" className="secondaryLink" style={styles.secondaryLink} data-testid="integration-hero-link-product">
                {t("common.viewProduct")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Advantage */}
      <section className="advantage" style={styles.advantage}>
        <div className="container">
          <div className="advantageHeader" style={styles.advantageHeader}>
            <h2 className="sectionTitle" style={styles.sectionTitle}>{t("integration.advantage.title")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("integration.advantage.subtitle")}</p>
          </div>
          <div className="advGrid" style={styles.advGrid}>
            {advItems.map((a, i) => {
              const Icon = ADV_ICONS[i] || Layers;
              return (
                <div key={i} className="stack-card advCard" style={styles.advCard} data-testid={`integration-adv-${i}`}>
                  <div className="advIcon" style={styles.advIcon}><Icon size={22} strokeWidth={1.75} /></div>
                  <div className="stack-title advTitle" style={styles.advTitle}>{a.title}</div>
                  <div className="stack-description advDesc" style={styles.advDesc}>{a.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="timelineSection" style={styles.timelineSection}>
        <div className="container">
          <div className="sectionHeader" style={styles.sectionHeader}>
            <h2 className="sectionTitleDark" style={styles.sectionTitleDark}>{t("integration.timeline.title")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("integration.timeline.subtitle")}</p>
          </div>
          <div className="timeline" style={styles.timeline}>
            {timelineItems.map((tt, i) => (
              <div key={i} className="timelineItem" style={styles.timelineItem} data-testid={`integration-timeline-${i}`}>
                <div className="timelineDot" style={styles.timelineDot} />
                <div className="timelineContent" style={styles.timelineContent}>
                  <div className="timelineWeek" style={styles.timelineWeek}>{tt.week}</div>
                  <div className="timelineTitle" style={styles.timelineTitle}>{tt.title}</div>
                  <div className="timelineDesc" style={styles.timelineDesc}>{tt.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modes - API vs No Code */}
      <section className="modesSection" style={styles.modesSection}>
        <div className="container">
          <div className="sectionHeader" style={styles.sectionHeader}>
            <h2 className="sectionTitle" style={styles.sectionTitle}>{t("integration.modes.titleLine1")}<br />{t("integration.modes.titleLine2")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("integration.modes.subtitle")}</p>
          </div>

          <div className="modesGrid" style={styles.modesGrid}>
            {modes.map((m, i) => {
              const Icon = MODE_ICONS[i] || Code2;
              return (
                <div key={i} className="modeCard" style={styles.modeCard} data-testid={`integration-mode-${i}`}>
                  <div className="modeIcon" style={styles.modeIcon}><Icon size={22} strokeWidth={1.75} /></div>
                  <div className="modeTag" style={styles.modeTag}>{m.tag}</div>
                  <h3 className="modeTitle" style={styles.modeTitle}>{m.title}</h3>
                  <div className="modeList" style={styles.modeList}>
                    {m.points.map((p, j) => {
                      const lower = String(p).toLowerCase();
                      const isDocPoint = lower.startsWith("documentaci") || lower.startsWith("complete documentation");
                      return (
                        <div key={j} className="modeItem" style={styles.modeItem}>
                          <Check size={18} color={i === 0 ? "#f5576c" : "#667eea"} />
                          {isDocPoint ? (
                            <a
                              href="https://caas-undostres.readme.io/reference"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="docLink" style={styles.docLink}
                              data-testid="integration-doc-link"
                            >
                              {p}
                            </a>
                          ) : (
                            <span>{p}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaSection" style={styles.ctaSection}>
        <div className="container">
          <div className="ctaInner" style={styles.ctaInner}>
            <h2 className="ctaTitle" style={styles.ctaTitle}>{t("integration.cta.title")}</h2>
            <p className="ctaSubtitle" style={styles.ctaSubtitle}>{t("integration.cta.subtitle")}</p>
            <Link to="/contacto">
              <button className="btn-primary ctaButton" style={styles.ctaButton} data-testid="integration-final-cta">
                {t("integration.cta.button")}
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: {
    padding: "160px 20px 80px",
    background: "#ffffff"
  },
  heroInner: { maxWidth: "900px", margin: "0 auto" },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "20px"
  },
  heroTitle: {
    fontSize: "64px",
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px"
  },
  heroSubtitle: {
    fontSize: "20px",
    lineHeight: 1.6,
    color: "#4a5568",
    maxWidth: "720px",
    marginBottom: "36px"
  },
  ctaRow: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap"
  },
  secondaryLink: {
    color: "#667eea",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "16px"
  },
  advantage: {
    padding: "100px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)"
  },
  advantageHeader: {
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto 48px"
  },
  sectionTitle: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "14px",
    letterSpacing: "-0.02em"
  },
  sectionTitleDark: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "14px",
    letterSpacing: "-0.02em"
  },
  sectionSubtitle: {
    fontSize: "18px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  advGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "24px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  advCard: {
    padding: "32px",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "16px"
  },
  advIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "#f1f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea",
    marginBottom: "18px"
  },
  advTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "8px",
    letterSpacing: "-0.01em"
  },
  advDesc: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  timelineSection: {
    padding: "100px 20px",
    background: "#ffffff"
  },
  sectionHeader: {
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto 60px"
  },
  timeline: {
    maxWidth: "820px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    position: "relative"
  },
  timelineItem: {
    display: "flex",
    gap: "24px",
    alignItems: "flex-start"
  },
  timelineDot: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 55%, #fda085 100%)",
    flexShrink: 0,
    marginTop: "8px"
  },
  timelineContent: {
    flex: 1,
    padding: "24px 28px",
    background: "#f8f9fc",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "14px"
  },
  timelineWeek: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#f5576c",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "6px"
  },
  timelineTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "6px",
    letterSpacing: "-0.01em"
  },
  timelineDesc: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  modesSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0,0,0,0.06)"
  },
  modesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "32px",
    maxWidth: "1100px",
    margin: "0 auto"
  },
  modeCard: {
    padding: "40px",
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#ffffff"
  },
  modeIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "#f1f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea",
    marginBottom: "20px"
  },
  modeTag: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#f5576c",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "10px"
  },
  modeTitle: {
    fontSize: "30px",
    fontWeight: 700,
    color: "#1a202c",
    letterSpacing: "-0.02em",
    marginBottom: "22px"
  },
  modeList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px"
  },
  modeItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "15px",
    color: "#2d3748"
  },
  docLink: {
    color: "#2d3748",
    textDecoration: "underline",
    textDecorationColor: "rgba(102, 126, 234, 0.5)",
    textUnderlineOffset: "3px"
  },
  ctaSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0,0,0,0.08)"
  },
  ctaInner: {
    maxWidth: "700px",
    margin: "0 auto",
    textAlign: "center"
  },
  ctaTitle: {
    fontSize: "52px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px",
    letterSpacing: "-0.02em"
  },
  ctaSubtitle: {
    fontSize: "19px",
    color: "#4a5568",
    lineHeight: 1.6,
    marginBottom: "36px"
  },
  ctaButton: {
    fontSize: "17px",
    padding: "16px 36px"
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    [data-testid="integration-page"] div[style*="grid-template-columns: repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; }
    [data-testid="integration-page"] div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
    [data-testid="integration-page"] h1 { font-size: 44px !important; }
    [data-testid="integration-page"] h2 { font-size: 38px !important; }
  }
  @media (max-width: 640px) {
    [data-testid="integration-page"] section:first-child { padding: 120px 16px 60px !important; }
    [data-testid="integration-page"] h1 { font-size: 34px !important; }
    [data-testid="integration-page"] div[style*="grid-template-columns: repeat(4"] { grid-template-columns: 1fr !important; }
    [data-testid="integration-page"] h2 { font-size: 30px !important; }
    [data-testid^="integration-mode-"] { padding: 28px !important; }
  }
`;
document.head.appendChild(styleSheet);

export default IntegrationPage;
